/**
 * Credit Check modal — product-aware credit request flow.
 * Mirrors Angular `credit-check-model` (Product, Contact, Mode, partner-specific APIs).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Envelope, EnvelopeSimple, Info, WarningCircle } from '@phosphor-icons/react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import opportunityInfoServices from '../../../../services/oneButtonServices/opportunityInfoService';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import DashboardModal from './DashboardModal';
import { OBDSelect } from './form-elements';
import { getPrimaryContactRecord } from './contactCreditStatus';
import {
    buildCreditCheckProductList,
    findProductCreditStatus,
    formatCreditCheckProductLabel,
    isIgsPartnerName,
    isLightreachOrSunlightPartnerName,
    isSunnovaPartnerName,
    resolveCreditCheckLeadId,
} from './creditCheckProductUtils';
import type {
    CreditCheckContactOption,
    CreditCheckMode,
    CreditCheckProductOption,
} from './CreditCheckModal.types';
import './oneButton-css/CreditCheckModal.css';
import { OBD_FINANCE_PARTNER_PROGRESS_Z, OBD_FINANCE_PARTNER_SYNC_Z } from './oneButtonOverlayZ';
import './oneButton-css/OneButtonFormControls.css';

const CREDIT_REDIRECT_URL = String(import.meta.env.VITE_CREDIT_REDIRECT_URL || '').trim();
const GOTO_SUNNOVA_URL = String(import.meta.env.VITE_GOTO_SUNNOVA_URL || '').trim();
const disableElectronicConsent =
    String(import.meta.env.VITE_DISABLE_ELECTRONIC_CONSENT || '').toLowerCase() === 'true';

export const CreditCheckModal: React.FC = () => {
    // ----- Store -----
    const open = useOneButtonStore((s) => s.creditCheckModalOpen);
    const setOpen = useOneButtonStore((s) => s.setCreditCheckModalOpen);
    const partnerNameFromStore = useOneButtonStore((s) => s.creditCheckPartnerName);
    const theme = useOneButtonStore((s) => s.theme);
    const opportunityId = useOneButtonStore((s) => s.opportunityID);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);

    // ----- Local state -----
    const [loading, setLoading] = useState(false);
    const [contacts, setContacts] = useState<CreditCheckContactOption[]>([]);
    const [productList, setProductList] = useState<CreditCheckProductOption[]>([]);
    const [selectedProductId, setSelectedProductId] = useState<string>('');
    const [selectedContactId, setSelectedContactId] = useState<string>('');
    const [mode, setMode] = useState<CreditCheckMode>('InPerson');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [isIGS, setIsIGS] = useState(false);
    const [isLightReachOrSunlight, setIsLightReachOrSunlight] = useState(false);
    const [isSunnova, setIsSunnova] = useState(false);
    const [isCheckCreditTriggered, setIsCheckCreditTriggered] = useState(false);
    const [showEligibilityWarning, setShowEligibilityWarning] = useState(false);
    const [creditContent, setCreditContent] = useState('');
    const [signingURL, setSigningURL] = useState('');
    const [iframeVisible, setIframeVisible] = useState(false);
    const [goToSunnova, setGoToSunnova] = useState<string | null>(null);
    const [busyText, setBusyText] = useState('Loading');

    const selectedProduct =
        productList.find((p) => String(p.Product_Type_Id) === String(selectedProductId)) || null;

    // ----- Helpers -----
    const normalizeSigningUrl = useCallback((rawUrl: string): string => {
        let url = String(rawUrl || '');
        if (CREDIT_REDIRECT_URL) {
            url = url.replace('http%3A%2F%2Flocalhost', CREDIT_REDIRECT_URL);
            url = url.replace(CREDIT_REDIRECT_URL, '');
        }
        return url;
    }, []);

    /**
     * Apply partner rules + primary contact / product-level sync contact selection
     * (Angular `onProductChange`).
     */
    const applyProductSelection = useCallback(
        (
            product: CreditCheckProductOption | null,
            contactsList: CreditCheckContactOption[],
            details: any,
        ) => {
            if (!product) {
                setIsIGS(false);
                setIsLightReachOrSunlight(false);
                setIsSunnova(false);
                return;
            }

            const partnerName = String(product.Finance_Partner_Display_Name || '');
            const igs = isIgsPartnerName(partnerName);
            const lightreachOrSunlight = isLightreachOrSunlightPartnerName(partnerName);
            const sunnova = isSunnovaPartnerName(partnerName);

            setIsIGS(igs);
            setIsLightReachOrSunlight(lightreachOrSunlight);
            setIsSunnova(sunnova);

            if (igs) {
                setMode('Email');
            } else {
                const productName = String(product.Product_Type_Name || '').trim().toLowerCase();
                const isLightreachBatteryOnly =
                    /light\s*reach/i.test(partnerName) && productName.startsWith('battery');
                if (isLightreachBatteryOnly) {
                    setMode('InPerson');
                } else {
                    setMode((current) => (current === 'InPerson' || current === 'Email' ? current : 'InPerson'));
                }
            }

            const primaryCustomer = getPrimaryContactRecord(details);
            if (primaryCustomer) {
                setEmail(String(primaryCustomer.Email ?? primaryCustomer.email ?? ''));

                const statusObj = findProductCreditStatus(details, product);
                let targetContactId =
                    statusObj?.Finance_Partner_Sync_ContactID ??
                    statusObj?.Finance_Partner_Sync_ContactId ??
                    statusObj?.finance_Partner_Sync_ContactID;

                if (!targetContactId) {
                    targetContactId =
                        primaryCustomer.Finance_Partner_Sync_ContactId ??
                        primaryCustomer.finance_Partner_Sync_ContactId;
                }

                const fullName = `${primaryCustomer.FirstName ?? ''} ${primaryCustomer.LastName ?? ''}`
                    .trim()
                    .toLowerCase();
                const targetIdStr = String(targetContactId ?? '').trim();

                let primaryContact = contactsList.find((c) => {
                    const matchId = c.contactId != null && String(c.contactId).trim() === targetIdStr;
                    const matchName = c.name && c.name.toLowerCase().trim() === fullName;
                    return matchId && matchName;
                });

                if (!primaryContact && contactsList.length === 1) {
                    primaryContact = contactsList[0];
                }

                // Angular lodash find requires both contactId + name; if no match, clear selection.
                if (!primaryContact && targetIdStr) {
                    primaryContact = contactsList.find(
                        (c) => c.contactId != null && String(c.contactId).trim() === targetIdStr,
                    );
                }
                if (!primaryContact && fullName) {
                    primaryContact = contactsList.find(
                        (c) => c.name && c.name.toLowerCase().trim() === fullName,
                    );
                }

                setSelectedContactId(primaryContact?.id != null ? String(primaryContact.id) : '');
            }
        },
        [],
    );

    const fetchContacts = useCallback(async () => {
        if (!opportunityId) {
            console.warn('CreditCheckModal: No opportunityId found in store');
            return;
        }

        setBusyText('Loading');
        setLoading(true);
        try {
            const products = buildCreditCheckProductList(opportunityDetails);
            setProductList(products);

            let initialProductId = '';
            if (products.length > 0) {
                if (partnerNameFromStore) {
                    const match = products.find((p) =>
                        String(p.Finance_Partner_Display_Name || '')
                            .toUpperCase()
                            .includes(partnerNameFromStore.toUpperCase()),
                    );
                    initialProductId = String((match || products[0]).Product_Type_Id);
                } else {
                    initialProductId = String(products[0].Product_Type_Id);
                }
            }
            setSelectedProductId(initialProductId);

            const customers = opportunityDetails?.Customers || [];
            const hasMissingSyncId = customers.some((c: any) => !c.Finance_Partner_Sync_ContactId);
            setShowEligibilityWarning(hasMissingSyncId);

            const res = await opportunityInfoServices.displayContacts(opportunityId);
            const contactsList = Array.isArray(res) ? res : [];
            const processedContacts: CreditCheckContactOption[] = contactsList.map((c: any, index: number) => ({
                id: String(index),
                contactId: c.contactId,
                name: c.name,
                email: c.email,
            }));
            setContacts(processedContacts);

            const initialProduct =
                products.find((p) => String(p.Product_Type_Id) === String(initialProductId)) || null;
            applyProductSelection(initialProduct, processedContacts, opportunityDetails);
        } catch (err) {
            console.error('CreditCheckModal: Failed to fetch contacts list, but continuing with store data', err);
        } finally {
            setLoading(false);
        }
    }, [opportunityId, opportunityDetails, partnerNameFromStore, applyProductSelection]);

    const refreshStatuses = useCallback(async (includeOppDetail = false) => {
        const currentOppId = useOneButtonStore.getState().opportunityID;
        if (!currentOppId) return;
        try {
            const syncRes = await quoteServices.getSyncStatusList(String(currentOppId));
            const syncList = syncRes?.FinancePartnerSyncStatuses || (Array.isArray(syncRes) ? syncRes : []);
            if (Array.isArray(syncList)) {
                useOneButtonStore.getState().setFinancePartnerRawSyncStatuses(syncList);
            }

            if (includeOppDetail) {
                const state = useOneButtonStore.getState();
                const salesforceId =
                    state.opportunityDetails?.SalesForce_Id ||
                    state.opportunityDetails?.Salesforce_Id ||
                    currentOppId;
                const oppDetail = await opportunityInfoServices.getOpportunityDetail(String(salesforceId));
                if (oppDetail) {
                    useOneButtonStore.getState().setOpportunityDetails(oppDetail);
                }
            }
        } catch (err) {
            console.error('CreditCheckModal: Failed to refresh dashboard status', err);
        }
    }, []);

    /** Reload agreements `RetrieveContract` credit statuses after close (Angular `afterClose` → `refreshData`). */
    const bumpAgreementsContractReload = useCallback(() => {
        useOneButtonStore.getState().setAgreementsRefreshTrigger(Date.now());
    }, []);

    const handleClose = useCallback(async () => {
        await refreshStatuses(isCheckCreditTriggered);
        bumpAgreementsContractReload();
        setOpen(false);
    }, [isCheckCreditTriggered, refreshStatuses, bumpAgreementsContractReload, setOpen]);

    const handleIframeClose = useCallback(async () => {
        setIframeVisible(false);
        await refreshStatuses(true);
        bumpAgreementsContractReload();
        setOpen(false);
    }, [refreshStatuses, bumpAgreementsContractReload, setOpen]);

    // ----- Effects -----
    useEffect(() => {
        if (open) {
            setMessage('');
            setError(null);
            setCreditContent('');
            setIsCheckCreditTriggered(false);
            setSigningURL('');
            setIframeVisible(false);
            setGoToSunnova(null);
            setSelectedProductId('');
            setSelectedContactId('');
            setProductList([]);
            setContacts([]);
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            fetchContacts();
        }
    }, [open, fetchContacts]);

    // ----- Handlers -----
    const handleProductChange = (val: string) => {
        setSelectedProductId(val);
        setError(null);
        setMessage('');
        setCreditContent('');
        setGoToSunnova(null);
        const product = productList.find((p) => String(p.Product_Type_Id) === String(val)) || null;
        applyProductSelection(product, contacts, opportunityDetails);
    };

    const handleContactChange = (val: string) => {
        setSelectedContactId(val);
        setError(null);
        const contact = contacts.find((c) => c.id === val);
        if (contact) {
            const matchedCustomer = (opportunityDetails?.Customers || []).find(
                (c: any) => String(c.Finance_Partner_Sync_ContactId) === String(contact.contactId),
            );
            if (matchedCustomer) setEmail(matchedCustomer.Email || '');
            else setEmail(contact.email || '');
        }
    };

    const handleModeChange = (val: string) => {
        setMode(val as CreditCheckMode);
        setError(null);
    };

    const openSigningIframe = (url: string) => {
        setSigningURL(normalizeSigningUrl(url));
        setIframeVisible(true);
    };

    const handleCheckCredit = async () => {
        if (!opportunityId) return;
        if (!selectedProduct) {
            setError('Please select a Product before clicking Check Credit');
            return;
        }
        if (!isIGS && (selectedContactId === '' || !mode)) {
            setError('Please ensure Credit Contact is filled before clicking Check Credit');
            return;
        }

        setBusyText('Loading');
        setLoading(true);
        setError(null);
        setCreditContent('');
        setMessage('');
        setGoToSunnova(null);

        try {
            let leadId = null;
            const financePartnerLeadList = opportunityDetails?.FinancePartnerLeadList || [];
            const matchedLead = financePartnerLeadList.find((ele: any) => ele.ProductTypeName === selectedProduct.Product_Type_Name || ele.ProductTypeName === selectedProduct.Product_Type_Name + ' Only' || ele.ProductTypeName + ' Only' === selectedProduct.Product_Type_Name);
            if (matchedLead?.Finance_Partner_Sync_LeadID) {
                leadId = matchedLead.Finance_Partner_Sync_LeadID;
            } else {
                leadId = selectedProduct?.Finance_Partner_Sync_LeadID ? selectedProduct.Finance_Partner_Sync_LeadID : (this.data?.leadDetails?.Finance_Partner_Sync_LeadID ? this.data?.leadDetails?.Finance_Partner_Sync_LeadID : this.data?.leadDetails?.OpportunityDetails?.Finance_Partner_Sync_LeadID);
            }

            if (isIGS) {
                const res = disableElectronicConsent
                    ? await opportunityInfoServices.getCreditEnquiryInvitation(String(leadId || ''))
                    : await opportunityInfoServices.ElectronicConsent(String(leadId || ''));

                if (res.IsSuccessful) {
                    setIsCheckCreditTriggered(true);
                    setMessage(
                        'The credit check request has been sent to IGS. Instruct the customer to check their email for the message from IGS to begin the process.',
                    );
                } else {
                    let errMsg = 'Credit check request failed, please try again';
                    try {
                        if (res.Message && typeof JSON.parse(res.Message) === 'object') {
                            errMsg = `${JSON.parse(res.Message)?.message} please try again`;
                        }
                    } catch {
                        errMsg = res.Message || errMsg;
                    }
                    setError(errMsg);
                    setIsCheckCreditTriggered(true);
                }
            } else if (isLightReachOrSunlight) {
                const contactData = contacts.find((c) => c.id === selectedContactId);
                const payload = {
                    opportunityId,
                    finance_Partner_Sync_contactId: contactData?.contactId,
                    finance_Partner_Sync_Id: leadId,
                    isEmail: mode.toLowerCase() === 'email',
                };

                const res = await opportunityInfoServices.checkCreditFinancePartner(payload);

                if (res.IsSuccessful) {
                    setIsCheckCreditTriggered(true);
                    if (mode === 'InPerson') {
                        openSigningIframe(res.Data);
                        setOpen(false);
                    } else {
                        setCreditContent(
                            'Credit check request sent to email on file. Please check your email to proceed further.',
                        );
                    }
                } else {
                    setError('Lightreach error while requesting Credit Check.');
                    setIsCheckCreditTriggered(true);
                }
            } else {
                // Sunnova (and other non-Lightreach partners using Sunnova check-credit)
                const contactData = contacts.find((c) => c.id === selectedContactId);
                const payload = {
                    ContactId: contactData?.contactId,
                    Action: mode.toLowerCase(),
                    opportunityId,
                };

                const checkCreditData1 = await opportunityInfoServices.checkCreditSunnova(payload);

                try {
                    const parsed =
                        typeof checkCreditData1 === 'string'
                            ? JSON.parse(checkCreditData1)
                            : checkCreditData1;

                    if (parsed && typeof parsed === 'object') {
                        setIsCheckCreditTriggered(true);
                        if (mode === 'InPerson') {
                            const url = String(parsed.signingURL || parsed.SigningURL || parsed.Data || '');
                            openSigningIframe(url);
                            setOpen(false);
                        } else {
                            setCreditContent(
                                'Credit check request sent to email on file. Please check your email to proceed further.',
                            );
                        }
                    } else {
                        const msg = checkCreditData1 ? `${checkCreditData1} -` : '';
                        setMessage(` Sunnova error while requesting Credit Check. ${msg}`);
                        setGoToSunnova(GOTO_SUNNOVA_URL || null);
                        setIsCheckCreditTriggered(true);
                    }
                } catch {
                    const msg = checkCreditData1 ? `${checkCreditData1} -` : '';
                    setMessage(` Error processing Credit Check. ${msg}`);
                    setGoToSunnova(GOTO_SUNNOVA_URL || null);
                    setIsCheckCreditTriggered(true);
                }
            }
        } catch (err: any) {
            console.error('CreditCheckModal: Submission Error', err);
            const customMessage = err?.customMessage || err?.message;
            if (isSunnova || (!isIGS && !isLightReachOrSunlight)) {
                setMessage(customMessage || 'Unknown Sunnova error while requesting Credit Check ');
                setGoToSunnova(GOTO_SUNNOVA_URL || null);
            } else {
                setError(customMessage || 'An unexpected error occurred.');
            }
        } finally {
            setLoading(false);
        }
    };

    const showForm =
        !isCheckCreditTriggered ||
        (mode === 'InPerson' && !isIGS) ||
        !!creditContent ||
        isLightReachOrSunlight;

    const contactOptions = contacts.map((c) => ({ value: c.id, label: c.name }));
    const productOptions = productList.map((p) => ({
        value: String(p.Product_Type_Id),
        label: formatCreditCheckProductLabel(p),
    }));
    const modeOptions = [
        { value: 'InPerson', label: 'In-Person' },
        { value: 'Email', label: 'Email' },
    ];
    const productDisabled = loading || !!creditContent;
    const contactAndModeDisabled = loading || isIGS || !!creditContent;
    const checkCreditDisabled = loading || (!isIGS && !selectedContactId) || !selectedProductId;

    // ----- Render -----
    return (
        <>
            <DashboardModal
                open={open}
                onClose={handleClose}
                title="Check Credit"
                width={720}
                maskClosable={false}
                zIndex={OBD_FINANCE_PARTNER_SYNC_Z}
                theme={theme}
                className={`obd-sync-modal obd-credit-check-modal obd-theme-${theme}`}
                rootClassName={`obd-sync-modal-root obd-credit-check-modal-root obd-theme-${theme}`}
                centered
                destroyOnClose
                footer={null}
                loading={loading}
                loadingMessage={busyText}
            >
                <div className="obd-sync-modal__inner" style={{ position: 'relative' }}>
                    {/* Header */}
                    <div className="obd-sync-modal__header">
                        <h3 className="obd-sync-modal__title">Check Credit</h3>
                    </div>

                    <div className="obd-sync-modal__content-body obd-credit-modal__body">
                        {/* Eligibility notice */}
                        {showEligibilityWarning && mode !== 'Email' && (
                            <div className="obd-credit-modal__eligibility-box">
                                <div className="obd-notice-content">
                                    <Info size={22} weight="fill" className="obd-notice-icon" />
                                    <div className="obd-notice-text">
                                        <p>
                                            Contacts must be added to contract and synced to finance partner for
                                            credit check eligibility
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {showForm ? (
                            <div className="obd-credit-modal__form">
                                {/* Product / Contact / Mode */}
                                <div className="obd-credit-modal__row">
                                    <div className="obd-credit-modal__field">
                                        <label>Product</label>
                                        <OBDSelect
                                            value={selectedProductId || undefined}
                                            onChange={handleProductChange}
                                            disabled={productDisabled}
                                            placeholder="Choose..."
                                            className="obd-select-field"
                                            style={{ width: '100%' }}
                                            options={productOptions}
                                        />
                                    </div>
                                    <div className="obd-credit-modal__field">
                                        <label>Credit Contact</label>
                                        <OBDSelect
                                            value={selectedContactId || undefined}
                                            onChange={handleContactChange}
                                            disabled={contactAndModeDisabled}
                                            placeholder="Choose..."
                                            className="obd-select-field"
                                            style={{ width: '100%' }}
                                            options={contactOptions}
                                        />
                                    </div>
                                    <div className="obd-credit-modal__field">
                                        <label>Mode</label>
                                        <OBDSelect
                                            value={mode}
                                            onChange={handleModeChange}
                                            disabled={contactAndModeDisabled}
                                            className="obd-select-field"
                                            style={{ width: '100%' }}
                                            options={modeOptions}
                                        />
                                    </div>
                                </div>

                                {/* Email notice */}
                                {mode === 'Email' && !creditContent && !message && (
                                    <div className="obd-credit-modal__notice-box">
                                        <div className="obd-notice-content">
                                            <WarningCircle size={22} weight="fill" className="obd-notice-icon" />
                                            <div className="obd-notice-text">
                                                <p>
                                                    Before proceeding, please ensure that the selected customer’s
                                                    contact information is correct. Once the Credit Check is
                                                    completed, any updates to the customer’s contact information will
                                                    need to be made by the Finance Partner.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="obd-credit-modal__field obd-credit-modal__email-field">
                                            <label>Recipient Email Address</label>
                                            <div className="obd-input-group">
                                                <Envelope size={18} className="obd-input-group__icon" />
                                                <input
                                                    type="email"
                                                    value={email}
                                                    readOnly={true}
                                                    className="obd-credit-modal__input"
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {creditContent && (
                                    <div className="obd-credit-modal__success-msg">{creditContent}</div>
                                )}

                                {error && (
                                    <div className="obd-credit-modal__error-message">
                                        <span>{error}</span>
                                    </div>
                                )}

                                {message && (
                                    <div className="obd-credit-modal__error-message">
                                        <span>{message}</span>
                                    </div>
                                )}

                                {goToSunnova && (
                                    <div className="obd-credit-modal__sunnova-link-wrap">
                                        <a
                                            href={goToSunnova}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="obd-sync-modal__btn obd-sync-modal__btn--primary"
                                        >
                                            Go To Sunnova
                                        </a>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="obd-credit-modal__success-state">
                                <div className="obd-credit-modal__envelop-wrapper">
                                    <EnvelopeSimple size={72} weight="fill" className="obd-envelop-svg-animated" />
                                </div>
                                <div className="obd-credit-modal__message-p">{message}</div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="obd-sync-modal__footer">
                        <div
                            className="obd-sync-modal__footer-btns"
                            style={{ width: '100%', justifyContent: 'center' }}
                        >
                            <button
                                type="button"
                                className="obd-sync-modal__btn obd-sync-modal__btn--secondary"
                                onClick={handleClose}
                            >
                                {isCheckCreditTriggered ? 'Close' : 'Cancel'}
                            </button>
                            {!isCheckCreditTriggered && !creditContent && (
                                <button
                                    type="button"
                                    className="obd-sync-modal__btn obd-sync-modal__btn--primary"
                                    onClick={handleCheckCredit}
                                    disabled={checkCreditDisabled}
                                >
                                    Check Credit
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </DashboardModal>

            {iframeVisible && (
                <DashboardModal
                    open={iframeVisible}
                    onClose={handleIframeClose}
                    footer={null}
                    width="90%"
                    maskClosable={false}
                    centered
                    destroyOnClose
                    zIndex={OBD_FINANCE_PARTNER_PROGRESS_Z}
                    title="Credit Check Form"
                    theme={theme}
                    className={`obd-credit-check-form-modal obd-theme-${theme}`}
                    rootClassName={`obd-form-controls-modal-root obd-credit-check-form-modal-root obd-theme-${theme}`}
                >
                    <div style={{ height: '80vh', position: 'relative' }}>
                        <iframe
                            src={signingURL}
                            style={{ width: '100%', height: '100%', border: 'none' }}
                            title="Credit Check"
                        />
                    </div>
                </DashboardModal>
            )}
        </>
    );
};

export default CreditCheckModal;
export type { CreditCheckContactOption, CreditCheckProductOption, CreditCheckMode } from './CreditCheckModal.types';
